import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { fetchStoreEditData, updateStore } from "../service/api/adminApi";
import {
    Store,
    Info,
    Map,
    Truck,
    Settings,
    ExternalLink,
    Coins,
    Percent,
    Wallet,
    SortAsc,
    Star,
    Save,
    CircleChevronRight,
} from "lucide-react";
import "../css/StoreEditpage.css";

const BACKEND_URL = "http://localhost:8000";

const tabs = [
    { id: "generalSettings", label: "General", icon: Store },
    { id: "metaDataSettings", label: "Meta Data", icon: Info },
    { id: "operationAreaSettings", label: "Operation Area & Zone", icon: Map },
    { id: "deliverySettings", label: "Delivery", icon: Truck },
    { id: "extraSettings", label: "Extras", icon: Settings },
    { id: "actionSettings", label: "Actions", icon: ExternalLink },
    { id: "paymentGatewaySettings", label: "Payment Gateways", icon: Coins },
    { id: "commissionSettings", label: "Commissions", icon: Percent },
    { id: "serviceFeeSettings", label: "Service Fee", icon: Percent },
    { id: "payoutDetailsSettings", label: "Payout Details", icon: Wallet },
    { id: "sortSettings", label: "Sort Menus and Items", icon: SortAsc },
    { id: "ratingSettings", label: "Rating & Reviews", icon: Star },
];

const requiredFields = new Set([
    "name",
    "description",
    "storeUrl",
    "fullAddress",
    "rating",
    "approxDeliveryTime",
    "approxPriceForTwo",
    "deliveryType",
    "deliveryChargeType",
    "commissionRate",
    "serviceFeeType",
    "serviceFee",
]);

const initialForm = {
    name: "",
    description: "",
    image: null,
    storeCharge: "",
    minOrderPrice: "",
    storeCategories: [],
    storeUrl: "",
    fullAddress: "",
    pincode: "",
    landmark: "",
    rating: "",
    approxDeliveryTime: "",
    approxPriceForTwo: "",
    certificateCode: "",
    customMessageHome: "",
    customMessageItems: "",
    zone: "",
    latitude: "",
    longitude: "",
    deliveryRadius: "",
    deliveryType: "1",
    deliveryChargeType: "FIXED",
    deliveryCharge: "",
    baseDeliveryCharge: "",
    baseDistance: "",
    extraDeliveryCharge: "",
    extraDistance: "",
    enableFreeDelivery: false,
    freeDeliverySubtotal: "",
    pureVeg: false,
    featured: false,
    promoted: false,
    customFeaturedBadgeName: "",
    autoAcceptOrder: false,
    smsNotification: false,
    automaticScheduling: false,
    acceptScheduledOrders: false,
    scheduleSlotBuffer: "",
    active: true,
    open: true,
    paymentGateways: [],
    commissionRate: "",
    serviceFeeType: "percent",
    serviceFee: "",
    bankName: "",
    bankCode: "",
    recipientName: "",
    accountNumber: "",
    paypalId: "",
    upiId: "",
};

const slugify = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const safeValue = (value) => (value === null || value === undefined ? "" : String(value));

const getStoreImageSrc = (store) => {
    const imageUrl = store?.image_url ? String(store.image_url).trim() : "";
    const image = store?.image ? String(store.image).trim() : "";

    const normalize = (value) =>
        String(value)
            .replace("https://localhost:8000", "http://localhost:8000")
            .replace("https://127.0.0.1:8000", "http://127.0.0.1:8000");

    const toImageSrc = (value) => {
        if (!value || value === "null" || value === "undefined") return "";

        const normalizedValue = normalize(value);

        if (
            normalizedValue.startsWith("http://") ||
            normalizedValue.startsWith("https://") ||
            normalizedValue.startsWith("//") ||
            normalizedValue.startsWith("blob:") ||
            normalizedValue.startsWith("data:")
        ) {
            return normalizedValue;
        }

        return normalizedValue.startsWith("/")
            ? `${BACKEND_URL}${normalizedValue}`
            : `${BACKEND_URL}/${normalizedValue}`;
    };

    return toImageSrc(imageUrl) || toImageSrc(image);
};

const getIds = (value) => {
    if (!Array.isArray(value)) return [];
    return value
        .map((item) => {
            if (typeof item !== "object" || item === null) return item;
            return item.id ?? item.payment_gateway_id ?? item.restaurant_category_id ?? item.category_id ?? item.value;
        })
        .filter((item) => item !== null && item !== undefined)
        .map(String);
};

const getList = (...lists) => lists.find((list) => Array.isArray(list)) || [];

const getName = (item) => item?.name || item?.title || item?.label || item?.area_name || item?.location_name || `#${item?.id || ""}`;

export default function StoreEditPage() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const defaultTab = useMemo(() => {
        const hash = location.hash?.replace("#", "");
        return tabs.some((tab) => tab.id === hash) ? hash : "generalSettings";
    }, [location.hash]);

    const [activeTab, setActiveTab] = useState(defaultTab);
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const [editData, setEditData] = useState(null);
    const [existingImageUrl, setExistingImageUrl] = useState("");
    const [newImagePreview, setNewImagePreview] = useState("");
    const [imageFailedUrl, setImageFailedUrl] = useState("");

    const imagePreviewSrc = newImagePreview || existingImageUrl;

    useEffect(() => {
        setActiveTab(defaultTab);
    }, [defaultTab]);

    const loadStoreEditData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetchStoreEditData(id);

            if (res?.success) {
                const data = res.data || {};
                const store = data.store || {};
                const serverImageUrl = getStoreImageSrc(store);

                setEditData(data);
                setExistingImageUrl(serverImageUrl);
                setNewImagePreview("");
                setImageFailedUrl("");

                setForm((current) => ({
                    ...current,
                    name: safeValue(store.name),
                    description: safeValue(store.description),
                    image: null,
                    storeCharge: safeValue(store.restaurant_charges),
                    minOrderPrice: safeValue(store.min_order_price),
                    storeCategories: getIds(data.selected_category_ids || data.selected_categories || store.category_ids || store.categories),
                    storeUrl: safeValue(store.slug),

                    fullAddress: safeValue(store.address),
                    pincode: safeValue(store.pincode),
                    landmark: safeValue(store.landmark),
                    rating: safeValue(store.rating),
                    approxDeliveryTime: safeValue(store.delivery_time),
                    approxPriceForTwo: safeValue(store.price_range),
                    certificateCode: safeValue(store.certificate),
                    customMessageHome: safeValue(store.custom_message_on_list),
                    customMessageItems: safeValue(store.custom_message),

                    zone: store.zone_id ? String(store.zone_id) : "",
                    latitude: safeValue(store.latitude),
                    longitude: safeValue(store.longitude),
                    deliveryRadius: safeValue(store.delivery_radius),

                    deliveryType: store.delivery_type ? String(store.delivery_type) : "1",
                    deliveryChargeType: safeValue(store.delivery_charge_type) || "FIXED",
                    deliveryCharge: safeValue(store.delivery_charges),
                    baseDeliveryCharge: safeValue(store.base_delivery_charge),
                    baseDistance: safeValue(store.base_delivery_distance),
                    extraDeliveryCharge: safeValue(store.extra_delivery_charge),
                    extraDistance: safeValue(store.extra_delivery_distance),
                    enableFreeDelivery: !!store.is_free_delivery,
                    freeDeliverySubtotal: safeValue(store.free_delivery_subtotal),

                    pureVeg: !!store.is_pureveg,
                    featured: !!store.is_featured,
                    promoted: !!store.is_promoted,
                    customFeaturedBadgeName: safeValue(store.custom_featured_name),
                    autoAcceptOrder: !!store.auto_acceptable,
                    smsNotification: !!store.is_notifiable,
                    automaticScheduling: !!store.is_schedulable,
                    acceptScheduledOrders: !!store.accept_scheduled_orders,
                    scheduleSlotBuffer: safeValue(store.schedule_slot_buffer),

                    paymentGateways: getIds(data.selected_payment_gateway_ids || data.selected_payment_gateways || data.store_payment_gateway_ids || data.store_payment_gateways || store.payment_gateway_ids || store.payment_gateways || store.store_payment_gateways),
                    commissionRate: safeValue(store.commission_rate),
                    serviceFeeType: safeValue(store.service_fee_type) || "percent",
                    serviceFee: safeValue(store.service_fee),

                    bankName: safeValue(data.payout_details?.bankName),
                    bankCode: safeValue(data.payout_details?.bankCode),
                    recipientName: safeValue(data.payout_details?.recipientName),
                    accountNumber: safeValue(data.payout_details?.accountNumber),
                    paypalId: safeValue(data.payout_details?.paypalId),
                    upiId: safeValue(data.payout_details?.upiID),
                }));
            }
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Failed to load store edit data");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadStoreEditData();
    }, [loadStoreEditData]);

    useEffect(() => {
        return () => {
            if (newImagePreview?.startsWith("blob:")) {
                URL.revokeObjectURL(newImagePreview);
            }
        };
    }, [newImagePreview]);

    const changeTab = (tabId) => {
        setActiveTab(tabId);
        window.history.replaceState(null, "", `/stores/edit/${id}#${tabId}`);
    };

    const handleChange = (event) => {
        const { name, value, multiple, selectedOptions } = event.target;

        setForm((current) => ({
            ...current,
            [name]: multiple ? Array.from(selectedOptions, (option) => option.value) : name === "storeUrl" ? slugify(value) : value,
        }));
    };

    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;
        setForm((current) => ({ ...current, [name]: checked }));
    };

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const nextPreview = URL.createObjectURL(file);
        setImageFailedUrl("");
        setNewImagePreview(nextPreview);
        setForm((current) => ({
            ...current,
            image: file,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const data = new FormData();
        const appendValue = (key, value) => {
            if (value === null || value === undefined) return;
            data.append(key, value);
        };
        const appendOptional = (key, value) => {
            if (value === null || value === undefined || value === "") return;
            data.append(key, value);
        };

        appendValue("name", form.name);
        appendValue("description", form.description);
        appendOptional("restaurant_charges", form.storeCharge);
        appendOptional("min_order_price", form.minOrderPrice);
        appendValue("store_url", form.storeUrl);
        appendValue("slug", form.storeUrl);

        appendValue("address", form.fullAddress);
        appendOptional("pincode", form.pincode);
        appendOptional("landmark", form.landmark);
        appendOptional("rating", form.rating);
        appendOptional("delivery_time", form.approxDeliveryTime);
        appendOptional("price_range", form.approxPriceForTwo);
        appendOptional("certificate", form.certificateCode);
        appendOptional("custom_message_on_list", form.customMessageHome);
        appendOptional("custom_message", form.customMessageItems);

        appendOptional("zone_id", form.zone);
        appendOptional("latitude", form.latitude);
        appendOptional("longitude", form.longitude);
        appendOptional("delivery_radius", form.deliveryRadius);

        appendValue("delivery_type", form.deliveryType);
        appendValue("delivery_charge_type", form.deliveryChargeType);
        appendOptional("delivery_charges", form.deliveryCharge);
        appendOptional("base_delivery_charge", form.baseDeliveryCharge);
        appendOptional("base_delivery_distance", form.baseDistance);
        appendOptional("extra_delivery_charge", form.extraDeliveryCharge);
        appendOptional("extra_delivery_distance", form.extraDistance);
        appendValue("is_free_delivery", form.enableFreeDelivery ? 1 : 0);
        appendOptional("free_delivery_subtotal", form.freeDeliverySubtotal);

        appendValue("is_pureveg", form.pureVeg ? 1 : 0);
        appendValue("is_featured", form.featured ? 1 : 0);
        appendValue("is_promoted", form.promoted ? 1 : 0);
        appendOptional("custom_featured_name", form.customFeaturedBadgeName);
        appendValue("auto_acceptable", form.autoAcceptOrder ? 1 : 0);
        appendValue("is_notifiable", form.smsNotification ? 1 : 0);
        appendValue("is_schedulable", form.automaticScheduling ? 1 : 0);
        appendValue("accept_scheduled_orders", form.acceptScheduledOrders ? 1 : 0);
        appendOptional("schedule_slot_buffer", form.scheduleSlotBuffer);

        appendOptional("commission_rate", form.commissionRate);
        appendValue("service_fee_type", form.serviceFeeType);
        appendOptional("service_fee", form.serviceFee);

        form.storeCategories.forEach((categoryId) => {
            data.append("category_ids[]", categoryId);
        });

        form.paymentGateways.forEach((gatewayId) => {
            data.append("payment_gateway_ids[]", gatewayId);
        });

        if (form.image) {
            data.append("image", form.image);
        }

        try {
            const res = await updateStore(id, data);
            alert(res.message || "Store updated successfully");
            loadStoreEditData();
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || error?.response?.data?.error || "Store update failed");
        }
    };

    const goToLegacyPage = (path) => {
        window.location.href = path;
    };

    const renderLabel = (name, label) => (
        <label htmlFor={name}>
            {requiredFields.has(name) && <span className="se-required">*</span>}
            {label}
        </label>
    );

    const renderSectionTitle = (title) => <h3 className="se-section-title">{title}</h3>;

    const renderTextInput = (name, label, placeholder = label, type = "text") => (
        <div className="se-form-row">
            {renderLabel(name, label)}
            <input id={name} name={name} type={type} value={safeValue(form[name])} onChange={handleChange} placeholder={placeholder} />
        </div>
    );

    const renderTextarea = (name, label, placeholder) => (
        <div className="se-form-row se-form-row-top">
            {renderLabel(name, label)}
            <textarea id={name} name={name} value={safeValue(form[name])} onChange={handleChange} placeholder={placeholder} rows={6} />
        </div>
    );

    const renderSelect = (name, label, options, placeholder) => (
        <div className="se-form-row">
            {renderLabel(name, label)}
            <select id={name} name={name} value={safeValue(form[name])} onChange={handleChange}>
                {placeholder && <option value="">{placeholder}</option>}
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );


    const renderSwitch = (name, label) => (
        <div className="se-switch-row">
            {renderLabel(name, label)}

            <label className="se-clean-toggle" htmlFor={name}>
                <input
                    id={name}
                    name={name}
                    type="checkbox"
                    checked={!!form[name]}
                    onChange={handleCheckboxChange}
                />
                <span></span>
            </label>
        </div>
    );

    if (loading) {
        return <div className="store-edit-page">Loading store...</div>;
    }

    const categoryOptions = getList(editData?.categories, editData?.restaurant_categories, editData?.restaurantCategories).map((category) => ({
        value: String(category.id),
        label: category.name,
    }));

    const zoneOptions = getList(editData?.zones, editData?.navZones).map((zone) => ({
        value: String(zone.id),
        label: zone.name,
    }));

    const paymentGatewayOptions = getList(
        editData?.admin_payment_gateways,
        editData?.adminPaymentGateways,
        editData?.payment_gateways,
        editData?.paymentGateways
    ).map((gateway) => ({
        value: String(gateway.id ?? gateway.payment_gateway_id ?? gateway.value),
        label: gateway.name || gateway.label || gateway.title || `Gateway #${gateway.id ?? gateway.payment_gateway_id ?? gateway.value}`,
    }));

    const deliveryAreas = getList(editData?.delivery_areas, editData?.deliveryAreas);
    const deliveryLocations = getList(editData?.delivery_locations, editData?.deliveryLocations);
    const storeName = form.name || `Store #${id}`;
    const storePublicUrl = `${BACKEND_URL}/stores/${form.storeUrl || "store-url"}`;
    const deliveryAreasUrl = editData?.delivery_areas_url || editData?.deliveryAreasUrl || `/admin/store/${id}/delivery-areas`;
    const deliveryLocationsUrl = editData?.delivery_locations_url || editData?.deliveryLocationsUrl || `/admin/store/${id}/delivery-locations`;

    const addCategory = (event) => {
        const value = event.target.value;
        if (!value) return;

        setForm((current) => ({
            ...current,
            storeCategories: current.storeCategories.includes(value) ? current.storeCategories : [...current.storeCategories, value],
        }));
        event.target.value = "";
    };

    const removeCategory = (categoryId) => {
        setForm((current) => ({
            ...current,
            storeCategories: current.storeCategories.filter((idValue) => idValue !== categoryId),
        }));
    };

    const renderCategoryTags = () => {
        const selectedCategories = form.storeCategories
            .map((categoryId) => categoryOptions.find((option) => option.value === categoryId))
            .filter(Boolean);
        const availableCategories = categoryOptions.filter((option) => !form.storeCategories.includes(option.value));

        return (
            <div className="se-form-row se-form-row-top">
                <label htmlFor="storeCategoryAdd">Store Categories</label>
                <div className="se-tag-selector">
                    <div className="se-tag-box">
                        {selectedCategories.length > 0 ? (
                            selectedCategories.map((category) => (
                                <span className="se-tag" key={category.value}>
                  {category.label}
                                    <button type="button" onClick={() => removeCategory(category.value)} aria-label={`Remove ${category.label}`}>
                    x
                  </button>
                </span>
                            ))
                        ) : (
                            <span className="se-tag-empty">No categories selected</span>
                        )}
                    </div>
                    <select id="storeCategoryAdd" value="" onChange={addCategory} className="se-tag-add-select">
                        <option value="">Add category</option>
                        {availableCategories.map((category) => (
                            <option key={category.value} value={category.value}>
                                {category.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    };
    const addPaymentGateway = (event) => {
        const value = event.target.value;
        if (!value) return;

        setForm((current) => ({
            ...current,
            paymentGateways: current.paymentGateways.includes(value) ? current.paymentGateways : [...current.paymentGateways, value],
        }));
        event.target.value = "";
    };

    const removePaymentGateway = (gatewayId) => {
        setForm((current) => ({
            ...current,
            paymentGateways: current.paymentGateways.filter((idValue) => idValue !== gatewayId),
        }));
    };

    const renderPaymentGatewayTags = () => {
        const selectedGateways = form.paymentGateways
            .map((gatewayId) => paymentGatewayOptions.find((option) => option.value === gatewayId))
            .filter(Boolean);
        const availableGateways = paymentGatewayOptions.filter((option) => !form.paymentGateways.includes(option.value));

        return (
            <div className="se-form-row se-form-row-top">
                <label htmlFor="paymentGatewayAdd">Select Payment Gateways</label>
                <div className="se-tag-selector">
                    <div className="se-tag-box">
                        {selectedGateways.length > 0 ? (
                            selectedGateways.map((gateway) => (
                                <span className="se-tag" key={gateway.value}>
                  {gateway.label}
                                    <button type="button" onClick={() => removePaymentGateway(gateway.value)} aria-label={`Remove ${gateway.label}`}>
                    x
                  </button>
                </span>
                            ))
                        ) : (
                            <span className="se-tag-empty">No payment gateways selected</span>
                        )}
                    </div>
                    <select id="paymentGatewayAdd" value="" onChange={addPaymentGateway} className="se-tag-add-select">
                        <option value="">Add payment gateway</option>
                        {availableGateways.map((gateway) => (
                            <option key={gateway.value} value={gateway.value}>
                                {gateway.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    };

    const renderBadgeList = (items) => (
        <div className="se-badge-list">
            {items.map((item) => (
                <span className="se-soft-badge" key={item.id || getName(item)}>
          {getName(item)}
        </span>
            ))}
        </div>
    );

    const renderOperationDataBox = ({ title, items, emptyText, buttonText, url }) => (
        <section className="se-operation-box">
            <div className="se-operation-box-header">
                <h4>{title}</h4>
                <button type="button" className="se-secondary-btn" onClick={() => goToLegacyPage(url)}>
                    {buttonText}
                </button>
            </div>
            {items.length > 0 ? renderBadgeList(items) : <p className="se-warning-text">{emptyText}</p>}
        </section>
    );

    const renderContent = () => {
        switch (activeTab) {
            case "generalSettings":
                return (
                    <>
                        {renderSectionTitle("General Settings")}
                        {renderTextInput("name", "Store Name", "Store Name")}
                        {renderTextInput("description", "Description", "Store Short Description")}

                        <div className="se-form-row se-form-row-top">
                            <label htmlFor="image">Image</label>
                            <div className="se-image-field">
                                <div className="se-image-preview">
                                    {imagePreviewSrc && imageFailedUrl !== imagePreviewSrc ? (
                                        <img src={imagePreviewSrc} alt="Store preview" onError={() => setImageFailedUrl(imagePreviewSrc)} />
                                    ) : (
                                        <span>No Image</span>
                                    )}
                                </div>
                                <div className="se-file-control">
                                    <span className="se-file-name">{form.image?.name || "No file selected"}</span>
                                    <label className="se-file-button" htmlFor="image">Choose File</label>
                                    <input id="image" name="image" type="file" accept="image/x-png,image/gif,image/jpeg,image/png,image/jpg" onChange={handleImageChange} />
                                </div>
                                <p className="se-help-text">Image dimension 160x117</p>
                            </div>
                        </div>

                        {renderTextInput(
                            "storeCharge",
                            "Store Charge (Packing/Extra):",
                            "Store Charge (Packing/Extra)"
                        )}
                        {renderTextInput("minOrderPrice", "Min Order Price", "Min Order Price")}
                        {renderCategoryTags()}
                        {renderTextInput("storeUrl", "Store URL", "store-url")}

                        <div className="se-form-row se-form-row-top">
                            <label>Store URL Preview</label>
                            <p className="se-url-preview">{storePublicUrl}</p>
                        </div>
                    </>
                );

            case "metaDataSettings":
                return (
                    <>
                        {renderSectionTitle("Meta Settings")}
                        {renderTextInput("fullAddress", "Full Address", "Full Address of Store")}
                        {renderTextInput("pincode", "Pincode", "Pincode / Postcode / Zip Code of Store")}
                        {renderTextInput("landmark", "Landmark", "Any Near Landmark")}
                        {renderTextInput("rating", "Rating", "Rating from 1-5")}
                        {renderTextInput("approxDeliveryTime", "Approx Delivery Time", "Time in Minutes")}
                        {renderTextInput("approxPriceForTwo", "Approx Price for Two", "Approx Price for 2 People")}
                        {renderTextInput("certificateCode", "Certificate/License Code", "Certificate Code or License Code")}
                        {renderTextarea("customMessageHome", "Custom Store Message Home Page", "Custom Store Message (Home Page) - Leave empty to hide")}
                        {renderTextarea("customMessageItems", "Custom Store Message Items Page", "Custom Store Message (Items Page) - Leave empty to hide")}
                    </>
                );

            case "operationAreaSettings":
                return (
                    <>
                        {renderSectionTitle("Operation Area Settings")}
                        {renderSelect("zone", "Zone", zoneOptions, "Select Zone")}
                        <div className="se-map-block">
                            <span className="se-map-dot" />
                            <span>Map preview will be connected later</span>
                        </div>
                        <div className="se-two-column-fields">
                            {renderTextInput("latitude", "Latitude", "Latitude")}
                            {renderTextInput("longitude", "Longitude", "Longitude")}
                        </div>
                        <section className="se-operation-box">
                            <h4>Radius Wise Area</h4>
                            {renderTextInput("deliveryRadius", "Delivery Radius in Km", "Delivery Radius")}
                        </section>
                        {renderOperationDataBox({
                            title: "Delivery Area Pro",
                            items: deliveryAreas,
                            emptyText: `${storeName} is not assigned to any area for operation.`,
                            buttonText: deliveryAreas.length > 0 ? "Manage Areas" : "Assign Areas",
                            url: deliveryAreasUrl,
                        })}
                        {renderOperationDataBox({
                            title: "Custom Delivery Locations",
                            items: deliveryLocations,
                            emptyText: `${storeName} is not assigned to any custom delivery location.`,
                            buttonText: deliveryLocations.length > 0 ? "Manage Locations" : "Assign Locations",
                            url: deliveryLocationsUrl,
                        })}
                    </>
                );

            case "deliverySettings":
                return (
                    <>
                        {renderSectionTitle("Delivery Settings")}
                        {renderSelect("deliveryType", "Delivery Type", [
                            { value: "1", label: "Delivery" },
                            { value: "2", label: "Self Pickup" },
                            { value: "3", label: "Both Delivery & Self Pickup" },
                        ])}
                        {renderSelect("deliveryChargeType", "Delivery Charge Type", [
                            { value: "FIXED", label: "FIXED" },
                            { value: "DYNAMIC", label: "DYNAMIC" },
                        ])}
                        {form.deliveryChargeType === "FIXED" ? (
                            renderTextInput("deliveryCharge", "Delivery Charge", "Delivery Charge")
                        ) : (
                            <>
                                <div className="se-four-fields-row">
                                    {renderTextInput("baseDeliveryCharge", "Base Delivery Charge", "Base Delivery Charge")}
                                    {renderTextInput("baseDistance", "Base Delivery Distance", "Base Delivery Distance")}
                                    {renderTextInput("extraDeliveryCharge", "Extra Delivery Charge", "Extra Delivery Charge")}
                                    {renderTextInput("extraDistance", "Extra Delivery Distance", "Extra Delivery Distance")}
                                </div>

                                <div className="se-form-row se-form-row-note">
                                    <label></label>
                                    <p className="se-help-text">
                                        Base delivery charges will be applied to the base delivery distance. And for every extra delivery distance, extra delivery charge will be applied.
                                    </p>
                                </div>
                            </>
                        )}
                        {renderSwitch("enableFreeDelivery", "Enable Free Delivery")}
                        {renderTextInput("freeDeliverySubtotal", "Free Delivery Subtotal", "Free Delivery Subtotal")}
                    </>
                );

            case "extraSettings":
                return (
                    <>
                        {renderSectionTitle("Extras")}
                        {renderSwitch("pureVeg", "Pure Veg")}
                        {renderSwitch("featured", "Featured")}
                        {renderSwitch("promoted", "Promoted")}
                        {renderTextInput("customFeaturedBadgeName", "Custom Featured Badge Name", "Custom Featured Badge Name")}
                        {renderSwitch("autoAcceptOrder", "Auto Accept Order")}
                        {renderSwitch("smsNotification", "SMS Notification")}
                        {renderSwitch("automaticScheduling", "Automatic Scheduling")}

                        <div className="se-form-row se-form-row-note">
                            <label></label>
                            <p className="se-help-text">
                                Learn more about Automatic Scheduling (Open/Close) time{" "}
                                <a
                                    href="https://docs.foodomaa.com/configurations/store-scheduling-open-close-times"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="se-help-link"
                                >
                                    here
                                </a>
                            </p>
                        </div>

                        {renderSwitch("acceptScheduledOrders", "Accept Scheduled Orders")}
                        {renderTextInput("scheduleSlotBuffer", "Schedule Slot Buffer", "Schedule Slot Buffer")}
                    </>
                );
            case "actionSettings":
                return (
                    <>
                        {renderSectionTitle("Action Settings")}

                        <div className="se-action-row">
                            <button
                                type="button"
                                className="se-secondary-btn"
                                title="The restaurant won't show up on customer's screen."
                            >
                                Deactivate / Activate
                            </button>

                            <button
                                type="button"
                                className="se-secondary-btn"
                                title="Users won't be able to place orders from this store if it is closed."
                            >
                                Open / Close
                            </button>
                        </div>

                        <p className="se-help-text">
                            Manual intervention on Open/Close will disable Automatic Scheduling if it was previously enabled.
                        </p>
                    </>
                );

            case "paymentGatewaySettings":
                return (
                    <>
                        {renderSectionTitle("Payment Gateways")}
                        {renderPaymentGatewayTags()}
                        {form.paymentGateways.length === 0 && (
                            <p className="se-warning-text"><strong>No Payment Gateways Active</strong><br />Admin selected Payment Gateways will be inherited.</p>
                        )}
                    </>
                );

            case "commissionSettings":
                return (
                    <>
                        {renderSectionTitle("Commissions")}
                        {renderTextInput("commissionRate", "Commission Rate %", "Commission Rate %")}
                    </>
                );

            case "serviceFeeSettings":
                return (
                    <>
                        {renderSectionTitle("Service Fee")}
                        {renderSelect("serviceFeeType", "Service Fee Type", [
                            { value: "percent", label: "Percentage" },
                            { value: "fixed", label: "Fixed Rate" },
                        ])}
                        {renderTextInput("serviceFee", "Service Fee", "Service Fee")}
                    </>
                );

            case "payoutDetailsSettings":
                return (
                    <>
                        {renderSectionTitle("Payout Details")}
                        {renderTextInput("bankName", "Bank Name", "Bank Name")}
                        {renderTextInput("bankCode", "Bank Code / IFSC", "Bank Code / IFSC")}
                        {renderTextInput("recipientName", "Recipient Name", "Recipient Name")}
                        {renderTextInput("accountNumber", "Account Number", "Account Number")}
                        {renderTextInput("paypalId", "Paypal ID", "Paypal ID")}
                        {renderTextInput("upiId", "UPI ID", "UPI ID")}
                        <div className="se-form-actions">
                            <button type="button" className="se-save-btn" onClick={() => alert("Update API not connected yet")}>
                                <Save size={17} />
                                Update Payout Details
                            </button>
                        </div>
                    </>
                );

            case "sortSettings":
                return (
                    <>
                        {renderSectionTitle("Sort Menus and Items")}
                        <button
                            type="button"
                            className="se-secondary-btn"
                            onClick={() => navigate(`/stores/edit/${id}/sort`)}
                        >
                            Sort Menus and Items
                        </button>
                    </>
                );

            case "ratingSettings":
                return (
                    <>
                        {renderSectionTitle("Rating & Reviews")}
                        <button
                            type="button"
                            className="se-secondary-btn"
                            onClick={() => navigate(`/stores/edit/${id}/ratings`)}
                        >
                            Rating & Reviews
                        </button>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <div className="store-edit-page">
            <form onSubmit={handleSubmit}>
                <div className="store-edit-header">
                    <div>
                        <h2 className="se-edit-title">Editing <CircleChevronRight size={18} /> {form.name || `#${id}`}</h2>
                        <p>Manage store details, delivery, payout and settings</p>
                    </div>
                </div>

                <div className="store-edit-card">
                    <aside className="store-edit-tabs">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    className={activeTab === tab.id ? "active" : ""}
                                    onClick={() => changeTab(tab.id)}
                                >
                                    <Icon size={17} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </aside>

                    <main className="store-edit-content">
                        {renderContent()}
                        <div className="se-bottom-actions">
                            <button type="submit" className="se-save-btn">
                                <Save size={17} />
                                Update Store
                            </button>
                        </div>
                    </main>
                </div>
            </form>
        </div>
    );
}